import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import axios from 'axios';
import {
    LayoutDashboard,
    BookOpen,
    TrendingUp,
    ClipboardCheck,
    FolderOpen,
    Video,
    LogOut,
    Menu,
    Users,
    UserPlus,
    HelpCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const menuGroups = [
    {
        label: 'MAIN',
        items: [
            { key: 'dashboard', label: 'Overview', icon: LayoutDashboard, path: '/admin' }
        ]
    },
    {
        label: 'USERS',
        items: [
            { key: 'users', label: 'Users', icon: Users, path: '/admin/users' },
            { key: 'invitations', label: 'Invites', icon: UserPlus, path: '/admin/invitations', showBadge: true }
        ]
    },
    {
        label: 'CONTENT',
        items: [
            { key: 'levels', label: 'Levels & Lessons', icon: BookOpen, path: '/admin/levels' },
            { key: 'insights', label: 'Market Insights', icon: TrendingUp, path: '/admin/insights' },
            { key: 'resources', label: 'Resources', icon: FolderOpen, path: '/admin/resources' },
            { key: 'sessions', label: 'Live Sessions', icon: Video, path: '/admin/sessions' }
        ]
    }
];

export const AdminLayout = ({ children }) => {
    const { logout, admin } = useAuth();
    const { language, toggleLanguage } = useLanguage();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeInvitations, setActiveInvitations] = useState(0);

    useEffect(() => {
        fetchInvitationCount();
    }, []);

    const fetchInvitationCount = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            const response = await axios.get(`${API}/admin/invitations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const active = response.data.filter(inv => inv.status === 'active').length;
            setActiveInvitations(active);
        } catch (error) {
            console.error('Error fetching invitations:', error);
        }
    };

    const isActive = (path) => {
        if (path === '/admin') {
            return location.pathname === '/admin';
        }
        return location.pathname.startsWith(path);
    };

    const NavContent = () => (
        <div className="flex flex-col h-full bg-slate-900">
            {/* Logo */}
            <div className="p-4 border-b border-slate-800">
                <Link to="/admin" className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                        <img 
                            src="/assets/logo.jpg" 
                            alt="Logo" 
                            className="w-8 h-8 rounded-lg object-contain"
                        />
                    </div>
                    <div>
                        <span className="font-heading font-bold text-base text-white block leading-tight">Admin Panel</span>
                        <span className="text-xs text-slate-500">Sekolah NusaTraders</span>
                    </div>
                </Link>
            </div>

            {/* Guide Link */}
            <div className="px-4 py-3 border-b border-slate-800">
                <Link 
                    to="/admin" 
                    className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                >
                    <HelpCircle className="w-4 h-4" />
                    <span className="text-sm">Panduan</span>
                </Link>
            </div>

            {/* Navigation */}
            <ScrollArea className="flex-1 px-3 py-4">
                {menuGroups.map((group, groupIdx) => (
                    <div key={groupIdx} className="mb-6">
                        <p className="text-xs font-medium text-slate-600 px-3 mb-2 tracking-wider">
                            {group.label}
                        </p>
                        <nav className="space-y-1">
                            {group.items.map((item) => (
                                <Link
                                    key={item.key}
                                    to={item.path}
                                    onClick={() => setMobileOpen(false)}
                                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                                        isActive(item.path)
                                            ? 'bg-primary text-white shadow-lg shadow-primary/25'
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                    }`}
                                    data-testid={`admin-nav-${item.key}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                        <span className="text-sm">{item.label}</span>
                                    </div>
                                    {item.showBadge && activeInvitations > 0 && (
                                        <Badge className="bg-primary/20 text-primary border-0 text-xs px-2">
                                            {activeInvitations}
                                        </Badge>
                                    )}
                                </Link>
                            ))}
                        </nav>
                    </div>
                ))}
            </ScrollArea>

            {/* Admin Info & Logout */}
            <div className="p-4 border-t border-slate-800">
                <div className="flex items-center gap-3 mb-3 px-2">
                    <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-primary font-semibold text-sm">
                            {admin?.name?.charAt(0).toUpperCase() || 'A'}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{admin?.name || 'Admin'}</p>
                        <p className="text-xs text-slate-500 truncate">{admin?.email}</p>
                    </div>
                </div>
                <Button
                    variant="ghost"
                    className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-900/20"
                    onClick={logout}
                    data-testid="admin-logout-btn"
                >
                    <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
                    Logout
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-950">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-slate-800">
                <NavContent />
            </aside>

            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800">
                <div className="flex items-center justify-between px-4 h-16">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-white" data-testid="admin-mobile-menu-btn">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="p-0 w-72 bg-slate-900 border-slate-800">
                            <NavContent />
                        </SheetContent>
                    </Sheet>

                    <Link to="/admin" className="flex items-center gap-2">
                        <img 
                            src="/assets/logo.jpg" 
                            alt="Logo" 
                            className="w-8 h-8 rounded-lg object-contain"
                        />
                        <span className="font-heading font-bold text-sm text-white">Admin</span>
                    </Link>

                    {/* Language Toggle */}
                    <div className="flex gap-1 bg-slate-800 rounded-full p-1">
                        <button
                            onClick={() => language !== 'id' && toggleLanguage()}
                            className={`px-2 py-1 text-xs rounded-full ${language === 'id' ? 'bg-primary text-white' : 'text-slate-400'}`}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => language !== 'en' && toggleLanguage()}
                            className={`px-2 py-1 text-xs rounded-full ${language === 'en' ? 'bg-primary text-white' : 'text-slate-400'}`}
                        >
                            EN
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="lg:pl-64">
                {/* Top Bar (Desktop) */}
                <div className="hidden lg:flex items-center justify-end px-8 py-3 bg-slate-900/50 border-b border-slate-800">
                    {/* Language Toggle */}
                    <div className="flex gap-1 bg-slate-800 rounded-full p-1">
                        <button
                            onClick={() => language !== 'id' && toggleLanguage()}
                            className={`px-3 py-1 text-sm rounded-full transition-colors ${language === 'id' ? 'bg-primary text-white' : 'text-slate-400 hover:text-white'}`}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => language !== 'en' && toggleLanguage()}
                            className={`px-3 py-1 text-sm rounded-full transition-colors ${language === 'en' ? 'bg-primary text-white' : 'text-slate-400 hover:text-white'}`}
                        >
                            EN
                        </button>
                    </div>
                </div>

                {/* Page Content */}
                <div className="p-4 lg:p-6 pt-20 lg:pt-6">
                    {children}
                </div>
            </main>
        </div>
    );
};
